import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { ArrowLeft, User, Trash2 } from 'lucide-react';
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { ProjectComputationDetail } from "./ProjectComputationDetail";

const ProjectComputation = ({ axiosInstance, currentUser }) => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('accounts'); // 'accounts', 'projects', or 'detail'
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedComputationId, setSelectedComputationId] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [computations, setComputations] = useState([]);
  const [projectDetails, setProjectDetails] = useState([]); // Store project details
  const [loading, setLoading] = useState(false);

  // Delete State
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  
  // Project Delete State
  const [isProjectDeleteDialogOpen, setIsProjectDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  useEffect(() => {
    fetchData();

    // Listen for project updates to refresh data
    const handleProjectUpdate = () => {
        fetchData();
    };
    window.addEventListener('projectUpdated', handleProjectUpdate);

    return () => {
        window.removeEventListener('projectUpdated', handleProjectUpdate);
    };
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all computations
      const compResponse = await axiosInstance.get('/ccp/project-computations');
      const allComputations = compResponse.data;

      // Fetch all project details for approval status
      const detailsResponse = await axiosInstance.get('/ccp/project-details');
      const allDetails = detailsResponse.data;
      setProjectDetails(allDetails);
      
      // Helper to check approval
      const isCadfUser = currentUser?.email?.trim().toLowerCase() === 'cadf@isourceasiaworkplace.com';
      const isOamUser = currentUser?.email?.trim().toLowerCase() === 'oam@isourceasiaworkplace.com';
      const isJcpUser = currentUser?.email?.trim().toLowerCase() === 'jcp@isourceasia.com';
      const isMspUser = currentUser?.email?.trim().toLowerCase() === 'msp@isourceasia.com.ph';

      // Group unique accounts from computations
      const uniqueAccountsMap = new Map();
      allComputations.forEach(comp => {
        if (comp.bdo_id && !uniqueAccountsMap.has(comp.bdo_id)) {
          uniqueAccountsMap.set(comp.bdo_id, {
            id: comp.bdo_id,
            name: comp.bdo_name || 'Unknown User',
            projectCount: 0,
            pendingCount: 0, // Initialize pending count
            exportReadyCount: 0 // Initialize export ready count
          });
        }
        
        if (comp.bdo_id && uniqueAccountsMap.has(comp.bdo_id)) {
            const acc = uniqueAccountsMap.get(comp.bdo_id);
            acc.projectCount += 1;

            // Find matching project detail
            const detail = allDetails.find(d => d.id === comp.project_detail_id);

            // Calculate pending count
            if (detail) {
                if (isCadfUser) {
                    const isApproved = detail.is_approved === true || detail.is_approved === 'true' || detail.is_approved === 1;
                    const isApprovedPricing = detail.is_approved_pricing === true || detail.is_approved_pricing === 'true' || detail.is_approved_pricing === 1;
                    if (isApproved && !isApprovedPricing) {
                        acc.pendingCount += 1;
                    }
                } else if (isOamUser) {
                    const isApprovedPricing = detail.is_approved_pricing === true || detail.is_approved_pricing === 'true' || detail.is_approved_pricing === 1;
                    const isApprovedCosts = detail.is_approved_costs === true || detail.is_approved_costs === 'true' || detail.is_approved_costs === 1;
                    if (isApprovedPricing && !isApprovedCosts) {
                        acc.pendingCount += 1;
                    }
                    
                    // Calculate Export Ready Count for OAM
                    const isApprovedOverall = detail.is_approved_overall === true || detail.is_approved_overall === 'true' || detail.is_approved_overall === 1;
                    const isExported = detail.is_exported_by_oam === true || detail.is_exported_by_oam === 'true' || detail.is_exported_by_oam === 1;
                    
                    if (isApprovedOverall && !isExported) {
                        acc.exportReadyCount += 1;
                    }

                } else if (isJcpUser) {
                    const isApprovedCosts = detail.is_approved_costs === true || detail.is_approved_costs === 'true' || detail.is_approved_costs === 1;
                    const isApprovedJoygie = detail.is_approved_joygie === true || detail.is_approved_joygie === 'true' || detail.is_approved_joygie === 1;
                    if (isApprovedCosts && !isApprovedJoygie) {
                        acc.pendingCount += 1;
                    }
                } else if (isMspUser) {
                    const isApprovedJoygie = detail.is_approved_joygie === true || detail.is_approved_joygie === 'true' || detail.is_approved_joygie === 1;
                    const isApprovedOverall = detail.is_approved_overall === true || detail.is_approved_overall === 'true' || detail.is_approved_overall === 1;
                    if (isApprovedJoygie && !isApprovedOverall) {
                        acc.pendingCount += 1;
                    }
                }
            }
        }
      });
      
      setAccounts(Array.from(uniqueAccountsMap.values()));
      setComputations(allComputations);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load project computations');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountClick = (account) => {
    setSelectedAccount(account);
    setViewMode('projects');
  };

  const handleBackToAccounts = () => {
    setSelectedAccount(null);
    setViewMode('accounts');
  };

  const confirmDeleteAccount = (account) => {
    setAccountToDelete(account);
    setIsDeleteDialogOpen(true);
  };

  const executeDeleteAccount = async () => {
    if (!accountToDelete) return;

    try {
      // Find all computations associated with this account (BDO)
      const computationsToDelete = computations.filter(c => c.bdo_id === accountToDelete.id);
      
      // Delete all of them in parallel
      const deletePromises = computationsToDelete.map(comp => 
        axiosInstance.delete(`/ccp/project-computations/${comp.id}`)
      );

      await Promise.all(deletePromises);

      // Update local state
      const remainingComputations = computations.filter(c => c.bdo_id !== accountToDelete.id);
      setComputations(remainingComputations);
      
      // Re-calculate accounts list from remaining computations
      const uniqueAccountsMap = new Map();
      remainingComputations.forEach(comp => {
        if (comp.bdo_id && !uniqueAccountsMap.has(comp.bdo_id)) {
          uniqueAccountsMap.set(comp.bdo_id, {
            id: comp.bdo_id,
            name: comp.bdo_name || 'Unknown User',
            projectCount: 0
          });
        }
        if (comp.bdo_id && uniqueAccountsMap.has(comp.bdo_id)) {
            const acc = uniqueAccountsMap.get(comp.bdo_id);
            acc.projectCount += 1;
        }
      });
      setAccounts(Array.from(uniqueAccountsMap.values()));
      
      toast.success('Account computations removed successfully');
      setIsDeleteDialogOpen(false);
      setAccountToDelete(null);
    } catch (error) {
      console.error('Error deleting account computations:', error);
      toast.error('Failed to remove account computations');
    }
  };

  const confirmDeleteProject = (project) => {
    setProjectToDelete(project);
    setIsProjectDeleteDialogOpen(true);
  };

  const executeDeleteProject = async () => {
    if (!projectToDelete) return;

    try {
        await axiosInstance.delete(`/ccp/project-computations/${projectToDelete.id}`);
        
        // Update local state
        const remainingComputations = computations.filter(c => c.id !== projectToDelete.id);
        setComputations(remainingComputations);

        // Re-calculate accounts list from remaining computations
        const uniqueAccountsMap = new Map();
        remainingComputations.forEach(comp => {
            if (comp.bdo_id && !uniqueAccountsMap.has(comp.bdo_id)) {
                uniqueAccountsMap.set(comp.bdo_id, {
                    id: comp.bdo_id,
                    name: comp.bdo_name || 'Unknown User',
                    projectCount: 0
                });
            }
            if (comp.bdo_id && uniqueAccountsMap.has(comp.bdo_id)) {
                const acc = uniqueAccountsMap.get(comp.bdo_id);
                acc.projectCount += 1;
            }
        });
        setAccounts(Array.from(uniqueAccountsMap.values()));

        toast.success('Project computation removed successfully');
        setIsProjectDeleteDialogOpen(false);
        setProjectToDelete(null);
    } catch (error) {
        console.error('Error deleting project computation:', error);
        toast.error('Failed to remove project computation');
    }
  };

  // Filter computations for selected account
  const filteredComputations = selectedAccount 
    ? computations.filter(c => c.bdo_id === selectedAccount.id)
    : [];

  const handleComputationClick = (comp) => {
    setSelectedComputationId(comp.id);
    setViewMode('detail');
  };

  const handleBackFromDetail = () => {
    setSelectedComputationId(null);
    setViewMode('projects');
  };

  if (viewMode === 'detail' && selectedComputationId) {
    return (
      <ProjectComputationDetail 
        axiosInstance={axiosInstance} 
        computationId={selectedComputationId} 
        onBack={handleBackFromDetail}
        currentUser={currentUser}
      />
    );
  }

  if (viewMode === 'accounts') {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Project Computation</h2>
          <p className="text-gray-600 dark:text-gray-200">Select an account to view project computations</p>
        </div>

        {loading ? (
           <div className="text-center py-12">
             <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
             <p className="text-gray-500">Loading accounts...</p>
           </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {accounts.length === 0 ? (
              <div className="col-span-full text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
                <p className="text-gray-500 dark:text-gray-400">No project computations found.</p>
              </div>
            ) : (
              accounts.map((account) => (
                <Card 
                  key={account.id}
                  className="glass shadow-md hover:shadow-xl transition-all cursor-pointer group border-l-4 border-l-indigo-500 relative"
                  onClick={() => handleAccountClick(account)}
                >
                  <CardContent className="p-6 flex flex-col items-center justify-center min-h-[140px]">
                    {(currentUser?.email?.trim().toLowerCase() === 'cadf@isourceasiaworkplace.com' || currentUser?.email?.trim().toLowerCase() === 'oam@isourceasiaworkplace.com' || currentUser?.email?.trim().toLowerCase() === 'jcp@isourceasia.com' || currentUser?.email?.trim().toLowerCase() === 'msp@isourceasia.com.ph') && account.pendingCount > 0 && (
                        <div className="absolute top-2 right-2 bg-blue-600 text-white rounded-full px-2 py-0.5 text-xs font-bold shadow-sm z-10">
                          {account.pendingCount}
                        </div>
                    )}
                    {currentUser?.email?.trim().toLowerCase() === 'oam@isourceasiaworkplace.com' && account.exportReadyCount > 0 && (
                        <div className="absolute top-2 right-10 bg-green-600 text-white rounded-full px-2 py-0.5 text-xs font-bold shadow-sm z-10">
                          {account.exportReadyCount}
                        </div>
                    )}
                    <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <User className="h-8 w-8 text-indigo-600 dark:text-indigo-300" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-1">
                      {account.name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {account.projectCount} Project{account.projectCount !== 1 ? 's' : ''}
                    </p>

                    {/* Remove button at bottom right */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDeleteAccount(account);
                      }}
                      className="absolute bottom-2 right-2 text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-8 w-auto px-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Account Computations</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete all computations for <strong>{accountToDelete?.name}</strong>? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={executeDeleteAccount}>Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Projects View
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={handleBackToAccounts} className="pl-0 hover:pl-2 transition-all">
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Accounts
            </Button>
            <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{selectedAccount?.name}</h2>
                <p className="text-gray-600 dark:text-gray-200">Project Computations</p>
            </div>
        </div>
      </div>

      {/* Tiles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredComputations.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
            <p className="text-gray-500 dark:text-gray-400">No computations found for this account.</p>
          </div>
        ) : (
          filteredComputations.map((comp) => {
            const isCadfUser = currentUser?.email?.trim().toLowerCase() === 'cadf@isourceasiaworkplace.com';
            const isOamUser = currentUser?.email?.trim().toLowerCase() === 'oam@isourceasiaworkplace.com';
            const isJcpUser = currentUser?.email?.trim().toLowerCase() === 'jcp@isourceasia.com';
            const isMspUser = currentUser?.email?.trim().toLowerCase() === 'msp@isourceasia.com.ph';
            
            let showBadge = false;
            let showGreenBadge = false;

            const detail = projectDetails.find(d => d.id === comp.project_detail_id);
            const isApproved = detail ? (detail.is_approved === true || detail.is_approved === 'true' || detail.is_approved === 1) : false;
            const isApprovedPricing = detail ? (detail.is_approved_pricing === true || detail.is_approved_pricing === 'true' || detail.is_approved_pricing === 1) : false;
            const isApprovedCosts = detail ? (detail.is_approved_costs === true || detail.is_approved_costs === 'true' || detail.is_approved_costs === 1) : false;
            const isDisapprovedCosts = detail ? (detail.is_disapproved_costs === true || detail.is_disapproved_costs === 'true' || detail.is_disapproved_costs === 1) : false;
            const isApprovedJoygie = detail ? (detail.is_approved_joygie === true || detail.is_approved_joygie === 'true' || detail.is_approved_joygie === 1) : false;
            const isApprovedOverall = detail ? (detail.is_approved_overall === true || detail.is_approved_overall === 'true' || detail.is_approved_overall === 1) : false;
            const approvedCount = [isApproved, isApprovedPricing, isApprovedCosts, isApprovedJoygie, isApprovedOverall].filter(Boolean).length;

            const statusText = isDisapprovedCosts
              ? 'Project Disapproved'
              : approvedCount === 5
                ? 'Project Approved'
                : `Checking ${approvedCount * 20}%`;

            const statusClassName = isDisapprovedCosts
              ? 'text-red-600 font-bold'
              : approvedCount === 5
                ? 'text-green-600 font-bold'
                : 'text-yellow-500 font-bold';
            
            if (isDisapprovedCosts) {
                showBadge = false;
                showGreenBadge = false;
            } else if (isCadfUser) {
                showBadge = isApproved && !isApprovedPricing;
            } else if (isOamUser) {
                showBadge = isApprovedPricing && !isApprovedCosts;
                const isExported = detail ? (detail.is_exported_by_oam === true || detail.is_exported_by_oam === 'true' || detail.is_exported_by_oam === 1) : false;
                if (isApprovedOverall && !isExported) {
                    showGreenBadge = true;
                }
            } else if (isJcpUser) {
                showBadge = isApprovedCosts && !isApprovedJoygie;
            } else if (isMspUser) {
                showBadge = isApprovedJoygie && !isApprovedOverall;
            }

            return (
            <Card 
              key={comp.id} 
              className="glass shadow-md hover:shadow-lg transition-all cursor-pointer group border-l-4 border-l-blue-500"
              onClick={() => handleComputationClick(comp)}
            >
              <CardContent className="p-6 flex items-center justify-between min-h-[120px] relative">
                {showBadge && (
                    <div className="absolute top-2 right-2 bg-blue-600 text-white rounded-full px-2 py-0.5 text-xs font-bold shadow-sm z-10">
                      1
                    </div>
                )}
                {showGreenBadge && (
                    <div className="absolute top-2 right-10 bg-green-600 text-white rounded-full px-2 py-0.5 text-xs font-bold shadow-sm z-10">
                      1
                    </div>
                )}

                <div className="flex flex-col justify-center text-left">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
                    {comp.project_name || comp.project_type || 'Untitled Project'}
                  </h3>
                  {comp.pcc_number && (
                      <p className="text-sm text-gray-500">{comp.pcc_number}</p>
                  )}
                </div>

                <div className="flex flex-col items-end justify-center text-right">
                  <p className={statusClassName}>{statusText}</p>
                  <p className="text-xs text-gray-400 mt-1">Computation ID: {comp.id.substring(0, 8)}...</p>
                </div>

                {/* Remove Project Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    confirmDeleteProject(comp);
                  }}
                  className="absolute bottom-2 right-2 text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-8 w-auto px-2 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Remove
                </Button>
              </CardContent>
            </Card>
            );
          })
        )}
      </div>

      {/* Project Delete Confirmation Dialog */}
      <Dialog open={isProjectDeleteDialogOpen} onOpenChange={setIsProjectDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Project Computation</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the computation for <strong>{projectToDelete?.project_name || projectToDelete?.project_type || 'Untitled Project'}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsProjectDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={executeDeleteProject}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectComputation;
